import { ref, computed, watch } from 'vue';
import KitchenTicket from '@/models/KitchenTicket';
import { useAppStore } from '@/store/useAppStore';
import moment from 'moment';

const audio = new Audio('/audio/alert2.mp3');
const firstLoad = ref(false);
const openPage = ref(1);
const donePage = ref(1);
const selectedItems = ref([]);
let fetchLatestInterval = null;

const useKitchenTickets = () => {
    const store = useAppStore();

    const yellowTimer = computed(() => store.state.kdsSettings.yellowTimer);
    const redTimer = computed(() => store.state.kdsSettings.redTimer);
    const ticketsPerPage = computed(() => store.state.kdsSettings.itemsPerPage[0] * store.state.kdsSettings.itemsPerPage[1]);
    const gridRows = computed(() => store.state.kdsSettings.itemsPerPage[0]);
    const gridCols = computed(() => store.state.kdsSettings.itemsPerPage[1]);

    const kitchenTickets = computed(() => {
        const tickets = [...KitchenTicket.all()].filter(kt => {
            if(store.state.user && store.state.kdsSettings.filterForUser) {
                return kt.user_id == store.state.user.id
            }

            return true
        })

        const sortedTickets = tickets.sort((a, b) => {
            return moment(b.issued_at).diff(moment(a.issued_at));
        });

        return sortedTickets.map((ticket) => {
            let warningColor = 'ongoing';
            const duration = moment.duration(moment().diff(moment(ticket.issued_at)));
            const hours = Math.floor(duration.asHours());
            const minutes = Math.floor(duration.asMinutes()) - hours * 60;
            const time = hours * 60 + minutes;

            if (time >= yellowTimer.value && time < redTimer.value) {
                warningColor = 'yellow';
            }

            if (time >= redTimer.value) {
                warningColor = 'red';
            }

            return {
                id: ticket.hash,
                ticket: ticket,
                warningColor: warningColor,
                page: 1,
            };
        });
    });

    const openTickets = computed(() => {
        const filtered = kitchenTickets.value.filter((ticket) => ticket.ticket.isPending());
        return filtered.map((ticket, index) => {
            ticket.page = Math.floor(index / ticketsPerPage.value) + 1;
            return ticket;
        });
    });

    const doneTickets = computed(() => {
        const filtered = kitchenTickets.value.filter((ticket) => ticket.ticket.isDone());
        return filtered.map((ticket, index) => {
            ticket.page = Math.floor(index / ticketsPerPage.value) + 1;
            return ticket;
        });
    });

    const openTicketsCount = computed(() => openTickets.value.length);
    const doneTicketsCount = computed(() => doneTickets.value.length);
    const totalOpenPages = computed(() => Math.ceil(openTicketsCount.value / ticketsPerPage.value));
    const totalDonePages = computed(() => Math.ceil(doneTicketsCount.value / ticketsPerPage.value));

    watch(
        () => kitchenTickets.value,
        (newValue, oldValue) => {
            if (newValue.length > oldValue.length) {
                audio.play();
            }
        }
    );

    watch(
        () => ticketsPerPage.value,
        () => {
            openPage.value = 1;
            donePage.value = 1;
        }
    );

    const fetchLatest = async () => {
        if (firstLoad.value) {
            return;
        } else {
            firstLoad.value = true;
        }

        fetchLatestInterval = setInterval(() => {
            KitchenTicket.fetchLatest();
        }, 60 * 1000);
    };

    const clearFetchLatestInterval = () => {
        firstLoad.value = false;

        if (fetchLatestInterval !== null) {
            clearInterval(fetchLatestInterval);
            fetchLatestInterval = null;
        }
    };

    const completeTicket = (ticket) => {
        const kitchenTicket = KitchenTicket.find(ticket.id);
        kitchenTicket.complete();
    };

    const recallTicket = (ticket) => {
        const kitchenTicket = KitchenTicket.find(ticket.id);
        kitchenTicket.recall();
    };

    const completeItem = (ticket, item, data, sync = true) => {
        const kitchenTicket = KitchenTicket.find(ticket.id);
        kitchenTicket.completeItem(item, data, sync);
    };

    const refreshTickets = () => {
        KitchenTicket.fetchLatest(true);
    };

    return {
        kitchenTickets,
        openTickets,
        doneTickets,
        openTicketsCount,
        doneTicketsCount,
        selectedItems,
        gridRows,
        gridCols,
        openPage,
        donePage,
        totalOpenPages,
        totalDonePages,
        fetchLatest,
        clearFetchLatestInterval,
        refreshTickets,
        completeTicket,
        recallTicket,
        completeItem,
    };
};

export default useKitchenTickets;
